import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import DesignPreview from '~/components/DesignPreview';
import ProductImage from '~/components/Product/ProductImage';
import RuledHeader from '~/shared/components/RuledHeader/index';
import Spinner from '~/components/SimpleSpinner';

import {getColor, getViewImage} from '~/utils/catalog';
import {sizeRange} from '~/utils/sizeHelpers';
import {Button, PlusCircleIcon, Typography} from '@customink/pigment-react';

import './MultiItemComplementCard.scss';

function renderProductImage({
  style,
  color,
  icons,
  onClick,
  selected,
  anchorStyle,
  getProductViews
}) {
  const view = getProductViews(anchorStyle)?.find(v => v?.name === 'front');
  const {uri} = getViewImage(color, 'front');
  if (view) {
    return (
      <div
        key="preview"
        className="ndx-MultiItemComplementCard-productImageContainer"
        onClick={onClick}>
        <DesignPreview
          className="ndx-MultiItemComplementCard-preview"
          view={view}
          colorId={color.id}
          icons={icons}
          style={style}
          viewIdentifier="front"
        />
      </div>
    );
  }

  return (
    <span
      key="preview"
      className="ndx-MultiItemComplementCard-productImageContainer">
      <ProductImage
        className="ndx-MultiItemComplementCard-preview"
        imagePath={uri}
        productId={color.id}
        showLoader={false}
        styleId={style.id}
        onClick={onClick}
      />
      {renderSelectedProduct(selected)}
    </span>
  );
}

function renderSelectedProduct(selected) {
  return selected ? <RuledHeader>Current Product</RuledHeader> : null;
}

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
function MultiItemComplementCard(props) {
  const {
    colorId,
    detailed,
    icons,
    style,
    selected,
    selectedProducts,
    onClick,
    anchorStyle,
    getProductViews
  } = props;

  const color = getColor(style, colorId);
  const isLoaded = !!(style && color);

  const className = classnames(
    'ndx-MultiItemComplementCard',
    'ndx-MultiItemComplementCard--small',
    {
      'ndx-MultiItemComplementCard--loading': !isLoaded
    }
  );

  return (
    <div
      className={className}
      data-testid="multi-item-complement-card"
      data-color-id={colorId}>
      {isLoaded ? (
        [
          <div
            key="description"
            className="ndx-MultiItemComplementCard-description">
            {renderProductImage({
              style,
              color,
              icons,
              onClick,
              selected,
              anchorStyle,
              getProductViews
            })}
            <div key="details" className="ndx-MultiItemComplementCard-details">
              <Typography
                component="div"
                variant="uiTextTiny"
                className="ndx-MultiItemComplementCard-details-title"
                data-testid="card-title">
                <strong>{props.title}</strong>
              </Typography>
              <Typography
                component="div"
                variant="uiTextSmall"
                className="ndx-MultiItemComplementCard-details-styleName"
                data-testid="style-name">
                {style.name}
              </Typography>
              {detailed && (
                <Typography component="p" variant="uiTextTiny">
                  Sizes: {sizeRange(style.sizes)}
                  <br />
                  {style.colors.filter(c => c.status === 'active').length}{' '}
                  colors
                </Typography>
              )}

              <Typography
                component="div"
                variant="uiTextTiny"
                className="ndx-MultiItemComplementCard-details-colorCount"
                data-testid="color-count">
                {`${
                  style.colors.filter(c => c.status === 'active').length
                } Colors Available`}
              </Typography>
            </div>
          </div>,
          <div key="add" className="ndx-MultiItemComplementCard-add">
            <Button
              variant="link"
              size="medium"
              startIcon={<PlusCircleIcon />}
              onClick={onClick}
              data-testid="add-style-button">
              {selectedProducts.includes(style.id)
                ? 'Add another color'
                : 'Add style'}{' '}
            </Button>
          </div>
        ]
      ) : (
        <Spinner />
      )}
    </div>
  );
}

const propTypes = {
  anchorStyle: PropTypes.object,
  colorId: PropTypes.string,
  detailed: PropTypes.bool,
  getActiveProductColors: PropTypes.func,
  getProductViews: PropTypes.func,
  icons: PropTypes.array,
  selected: PropTypes.bool,
  selectedProduct: PropTypes.string,
  selectedProducts: PropTypes.array,
  style: PropTypes.object,
  title: PropTypes.string,
  onClick: PropTypes.func
};

MultiItemComplementCard.propTypes = propTypes;

export default MultiItemComplementCard;
